import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  MdRefresh,
  MdDns,
  MdCloud,
  MdManageSearch,
  MdMemory,
  MdStorage,
  MdViewStream,
  MdPublic,
  MdKey,
} from "../../onam/icons"; // ONAM glyphs (was react-icons/md)
import { AuthContext } from "../../contexts/auth-context";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import LayoutContainer from "../LayoutContainer";
import { toTitleCase } from "../../utils";
import { HealthData, HealthResponse } from "./types";
import {
  collectHealthIssues,
  computeOverallSeverity,
  countHealthChecks,
  flattenSqsQueues,
  splitAuth0,
} from "./healthUtils";
import StatusBanner from "./StatusBanner";
import SectionCard from "./SectionCard";
import ServiceTile from "./ServiceTile";
import StatusPill from "./StatusPill";
import SqsQueueTable from "./SqsQueueTable";
import CloudfrontTable from "./CloudfrontTable";
import styles from "./systemHealth.module.css";

const SystemHealthShimmer: React.FC = () => (
  <div className={styles.sectionGrid}>
    <div className={`${styles.shimmerBlock} ${styles.shimmerBanner}`} />
    {/* Core Services / EKS */}
    {[6, 4].map((tileCount, i) => (
      <div key={i} className={styles.sectionCard}>
        <div className={`${styles.shimmerBlock} ${styles.shimmerCardHeader}`} />
        <div className={styles.tileGrid}>
          {Array.from({ length: tileCount }).map((_, j) => (
            <div key={j} className={`${styles.shimmerBlock} ${styles.shimmerTile}`} />
          ))}
        </div>
      </div>
    ))}
    {/* Indexing / Cache / Identity row */}
    <div className={styles.metaRow}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={`${styles.sectionCard} ${styles.metaCard}`}>
          <div className={`${styles.shimmerBlock} ${styles.shimmerCardHeader}`} />
          <div className={`${styles.shimmerBlock} ${styles.shimmerMetaItem}`} />
          <div className={`${styles.shimmerBlock} ${styles.shimmerMetaItem}`} />
        </div>
      ))}
    </div>
    {/* AWS / SQS / CDN */}
    {[5, 4, 3].map((tileCount, i) => (
      <div key={i} className={styles.sectionCard}>
        <div className={`${styles.shimmerBlock} ${styles.shimmerCardHeader}`} />
        {i === 0 ? (
          <div className={styles.tileGrid}>
            {Array.from({ length: tileCount }).map((_, j) => (
              <div key={j} className={`${styles.shimmerBlock} ${styles.shimmerTile}`} />
            ))}
          </div>
        ) : (
          <div className={`${styles.shimmerBlock} ${styles.shimmerTable}`} />
        )}
      </div>
    ))}
  </div>
);

const SystemHealth: React.FC = () => {
  const { getHealthCheck, refreshHealthCheck } = useContext(AuthContext);
  const { setBreadcrumbItems } = useBreadcrumb();

  const [health, setHealth] = useState<HealthData | null>(null);
  const [recordedAt, setRecordedAt] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "System Health", path: "/system-health" },
    ]);
  }, [setBreadcrumbItems]);

  // highlight System Health in the side navigation, as every other Curation page does for itself
  // (without this, the item of the page you came from stayed highlighted)
  const sidebar = useSidebar();
  useEffect(() => {
    if (!sidebar) return;
    const label = (sidebar.mainMenuItems || [])
      .flatMap((item: any) => item.subMenu || [])
      .find((sub: any) => (sub.urls || []).some((url: string) => url.includes("/system-health")))?.label;
    if (label) sidebar.setActiveSubItem(label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyResult = (result: HealthResponse) => {
    if (!result?.health) return;
    setHealth(result.health);
    setRecordedAt(result.recorded_at);
  };

  const loadHealth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getHealthCheck();
      applyResult(result);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch health data");
    } finally {
      setIsLoading(false);
    }
  }, [getHealthCheck]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const handleRefreshAll = async () => {
    setIsRefreshingAll(true);
    try {
      await refreshHealthCheck();
      // Give the backend a moment to persist the result, then fetch latest
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const latest = await getHealthCheck();
      applyResult(latest);
    } catch (err) {
      console.error("Refresh all failed:", err);
    } finally {
      setIsRefreshingAll(false);
    }
  };

  const issues = health ? collectHealthIssues(health) : [];
  const severity = health ? computeOverallSeverity(issues) : "operational";
  const totalChecks = health ? countHealthChecks(health) : 0;
  const sqsQueues = flattenSqsQueues(health?.sqs_queue_status);
  const { auth0, rest: awsServices } = splitAuth0(health?.aws_services);
  const issueCountByGroup = (group: string) =>
    issues.filter((issue) => issue.group === group).length;

  return (
    <LayoutContainer>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.pageHeader}>
            <h1 className={styles.pageTitle}>System Health</h1>
            <button
              type="button"
              className={styles.refreshAllBtn}
              onClick={handleRefreshAll}
              disabled={isRefreshingAll || isLoading}
            >
              <MdRefresh className={isRefreshingAll ? styles.spin : ""} />
              {isRefreshingAll ? "Fetching latest status…" : "Refresh All"}
            </button>
          </div>

          {error && !isLoading && !isRefreshingAll && (
            <p className={styles.error}>{error}</p>
          )}

          {(isLoading || isRefreshingAll) && <SystemHealthShimmer />}

          {!isLoading && !isRefreshingAll && !error && health && (
            <>
              <StatusBanner
                severity={severity}
                issues={issues}
                recordedAt={recordedAt}
                totalChecks={totalChecks}
              />

              <div className={styles.sectionGrid}>
                <SectionCard
                  title="Core Services"
                  accent="core"
                  icon={<MdDns />}
                  info="Client-facing API services, checked directly rather than through the EKS ingress layer."
                  issueCount={issueCountByGroup("Core Services")}
                >
                  <div className={styles.tileGrid}>
                    {Object.entries(health.services || {}).map(
                      ([key, item]) => (
                        <ServiceTile
                          key={key}
                          name={item.ui_display_name || toTitleCase(key)}
                          status={item.status}
                          latencyMs={item.latency_ms}
                          error={item.error}
                        />
                      ),
                    )}
                  </div>
                </SectionCard>

                <SectionCard
                  title="EKS Services"
                  accent="eks"
                  badge="EKS"
                  icon={<MdCloud />}
                  info="The same services as reached through the EKS ingress layer. Names overlap with Core Services, but this can fail independently on ingress-specific issues."
                  issueCount={issueCountByGroup("EKS Services")}
                >
                  <div className={styles.tileGrid}>
                    {Object.entries(health.eks_services || {}).map(
                      ([key, item]) => (
                        <ServiceTile
                          key={key}
                          name={item.ui_display_name || toTitleCase(key)}
                          status={item.status}
                          latencyMs={item.latency_ms}
                          error={item.error}
                        />
                      ),
                    )}
                  </div>
                </SectionCard>

                <div className={styles.metaRow}>
                  <SectionCard
                    title="Indexing"
                    accent="indexing"
                    icon={<MdManageSearch />}
                    info="Connectivity checks for the indexing pipeline: its ping endpoint and the Postgres database it depends on."
                    className={styles.metaCard}
                    issueCount={issueCountByGroup("Indexing")}
                  >
                    <div className={styles.metaList}>
                      <div className={styles.metaItem}>
                        <span>Ping</span>
                        <StatusPill status={health.indexing?.ping} />
                      </div>
                      <div className={styles.metaItem}>
                        <span>Postgres</span>
                        <StatusPill status={health.indexing?.postgres} />
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Cache Layer"
                    accent="cache"
                    icon={<MdMemory />}
                    info="Valkey, the Redis-compatible cache shared across services."
                    className={styles.metaCard}
                    issueCount={issueCountByGroup("Cache Layer")}
                  >
                    <ServiceTile
                      name={health.valkey?.ui_display_name || "Valkey"}
                      status={health.valkey?.status}
                      latencyMs={health.valkey?.latency_ms}
                      error={health.valkey?.error}
                    />
                  </SectionCard>

                  <SectionCard
                    title="Identity Provider"
                    accent="identity"
                    icon={<MdKey />}
                    info="Auth0, the third-party identity provider used for authentication. Kept separate from AWS Services since it isn't AWS-managed."
                    className={styles.metaCard}
                    issueCount={issueCountByGroup("Identity Provider")}
                  >
                    <ServiceTile
                      name={auth0?.ui_display_name || "Auth0"}
                      status={auth0?.status}
                      latencyMs={auth0?.latency_ms}
                      error={auth0?.error}
                    />
                  </SectionCard>
                </div>

                <SectionCard
                  title="AWS Services"
                  accent="aws"
                  icon={<MdStorage />}
                  info="Direct connectivity checks against AWS-managed dependencies."
                  issueCount={issueCountByGroup("AWS Services")}
                >
                  <div className={styles.tileGrid}>
                    {Object.entries(awsServices).map(([key, item]) => (
                      <ServiceTile
                        key={key}
                        name={item.ui_display_name || toTitleCase(key)}
                        status={item.status}
                        latencyMs={item.latency_ms}
                        error={item.error}
                      />
                    ))}
                  </div>
                </SectionCard>

                <SectionCard
                  title="SQS Queues"
                  accent="sqs"
                  icon={<MdViewStream />}
                  info="Connectivity and latency checks for each queue in the async processing pipeline."
                  issueCount={issueCountByGroup("SQS Queues")}
                >
                  <SqsQueueTable queues={sqsQueues} />
                </SectionCard>

                <SectionCard
                  title="CDN / CloudFront"
                  accent="cdn"
                  icon={<MdPublic />}
                  info="Distribution deploy status alongside a live HTTP health check against each domain."
                  issueCount={issueCountByGroup("CDN")}
                >
                  <CloudfrontTable rows={health.cloudfront || []} />
                </SectionCard>
              </div>
            </>
          )}
        </div>
      </div>
    </LayoutContainer>
  );
};

export default SystemHealth;
